import { useEffect } from 'react';
import type { CSSProperties } from 'react';

import * as d3 from 'd3';
import colors from '@/theme/colors';
import { Center, Flex, Text } from '@chakra-ui/react';
import { getColorFromPercentage } from '@/utils/getColorFromPercentage';

export type CircleFillOutWithTextProps = {
  svgId: string;
  circleColor?: string;
  circleDiameter?: number;
  percentageFilled: number;
  percentageTextStyle?: CSSProperties;
  thickness?: number;
  centeredText?: string;
  centeredElement?: JSX.Element;
  backgroundTransparent?: boolean;
  backgroundColor?: string;
  showPercentageText?: boolean;
  customPercentageText?: string;
  boxShadow?: string;
};

const CircleFillOutWithText = ({
  svgId: svgIdProp,
  circleColor,
  circleDiameter = 64,
  percentageFilled,
  percentageTextStyle = {
    color: 'text.darkBlue',
    fontWeight: 700,
    fontSize: 11,
  },
  thickness = 5,
  centeredText,
  centeredElement,
  backgroundTransparent = false,
  backgroundColor = 'white',
  showPercentageText = true,
  boxShadow = '0px 10px 20px 0px #0E006224',
  customPercentageText,
}: CircleFillOutWithTextProps) => {
  const outerRadius = circleDiameter / 2;
  const svgId = svgIdProp.replace(/ /g, '-').replace('|', '').toLowerCase();

  const circleArc = d3
    .arc<unknown>()
    .innerRadius(outerRadius - thickness)
    .outerRadius(outerRadius);

  const arcTween = (transition: any, newStartAngle: any, newFinishAngle: any) => {
    transition.attrTween('d', function (d: any) {
      const interpolateStart = d3.interpolate(d.startAngle, newStartAngle);
      return function (t: any) {
        d.endAngle = newFinishAngle;
        d.startAngle = interpolateStart(t);
        return circleArc(d);
      };
    });
  };

  useEffect(() => {
    // minus sign makes the circle fill counter-clockwise (as design requires)
    // Math.PI * 2 is the full circle; so we just multiply it with filled percentage value
    const ANGLE = -Math.PI * 2 * (percentageFilled / 100);

    d3.select(`#${svgId}`).selectChildren().remove(); // remove currently rendered circles to prevent overlays

    const selectedSvg = d3
      .select(`#${svgId}`)
      .append('g')
      .attr('transform', `translate(${outerRadius}, ${outerRadius})`);

    const svgForeground = selectedSvg
      .append('path')
      .datum({
        endAngle: 0,
        startAngle: 0,
      })
      .style('fill', circleColor || getColorFromPercentage(percentageFilled))
      .attr('d', circleArc);

    setTimeout(() => {
      svgForeground.transition().duration(1750).call(arcTween, ANGLE, 0.01); // 0.01 needed, because 0 makes it not animate...
    }, 300);
  }, [svgId, outerRadius, circleColor, percentageFilled]);

  return (
    <Flex
      position={'relative'}
      background={backgroundTransparent ? 'transparent' : backgroundColor}
      width={`${circleDiameter}px`}
      height={`${circleDiameter}px`}
      borderRadius={'50%'}
      boxShadow={boxShadow}
    >
      <Center position={'absolute'} flexDir={'column'} width={'100%'} height={'100%'}>
        {showPercentageText && (
          <Text style={percentageTextStyle}>{customPercentageText ?? percentageFilled.toFixed()}</Text>
        )}
        <Text color={colors.extra.white} paddingX={'25px'} textAlign={'center'} fontSize={14} fontWeight={700}>
          {centeredText}
        </Text>
        {centeredElement}
      </Center>
      <svg id={svgId}></svg>
    </Flex>
  );
};

export default CircleFillOutWithText;
